import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import { LoginManager } from '../../core/login-manager';
import { BookingManager, esitoAnnullamento, fuoriTermine } from '../../lib/booking-manager';
import { isAdmin } from '../../lib/ruoli';
import {
    formatDateTime, formatImporto, formatIntervallo, nomeUtente,
    optValue, optPayload, refLabel, toDateKey, umanizza
} from '../../lib/format';
import {
    useEntityItem, statoLista, Field, DetailPage, DetailActions, ConfirmModal, messaggioErrore,
    classePillStato
} from './common';
import Select from '../../components/Select';

const METODI = [
    { value: '', label: 'Non indicato' },
    { value: 'bonifico', label: 'Bonifico' },
    { value: 'contanti', label: 'Contanti' },
    { value: 'addebito_condominiale', label: 'Addebito condominiale' }
];

/**
 * Scheda di una prenotazione (`/prenotazioni/:id`), la stessa per il
 * condomino e per l'amministratore.
 *
 * Cambia cosa si vede, non come. Incasso e note interne sono roba
 * dell'amministrazione e per il condomino non esistono: lo stato del
 * pagamento si vede solo di qua, perche' il saldo si sistema fuori dalla
 * piattaforma e mostrarglielo qui prometterebbe un conto che non c'e'. Il
 * nome del condomino non glielo si ripete: la prenotazione e' la sua.
 *
 * L'annullamento passa dal BookingEngine (`cancel_booking`), che applica i
 * controlli di competenza e traccia data e motivo; l'incasso e' offline,
 * quindi il pagamento si registra a mano.
 */
export default function Prenotazione() {
    const { id } = useParams();
    const navigate = useNavigate();
    const amministratore = isAdmin(LoginManager.getUserInfo());
    const elenco = '/prenotazioni';
    const { item, loading, error, reload } = useEntityItem('prenotazione', id);

    const [pagato, setPagato] = useState(0);
    const [dataPagamento, setDataPagamento] = useState('');
    const [metodo, setMetodo] = useState('');
    const [noteAdmin, setNoteAdmin] = useState('');
    const [saving, setSaving] = useState(false);
    const [daAnnullare, setDaAnnullare] = useState(false);
    const [motivoAnnullamento, setMotivoAnnullamento] = useState('');
    const [working, setWorking] = useState(false);

    useEffect(() => {
        if (!item) return;
        setPagato(parseInt(item.pagato, 10) === 1 ? 1 : 0);
        setDataPagamento(item.data_pagamento || '');
        setMetodo(optValue(item.metodo_pagamento) || '');
        setNoteAdmin(item.note_admin || '');
    }, [item]);

    const st = item ? optValue(item.stato) : '';
    const annullabile = st === 'confermata';

    async function salva(e) {
        e.preventDefault();
        const incassato = parseInt(pagato, 10) === 1;
        const payload = {
            pagato: incassato ? 1 : 0,
            // segnando l'incasso senza data, vale la data odierna
            data_pagamento: incassato ? (dataPagamento || toDateKey(new Date())) : '',
            note_admin: noteAdmin
        };
        // un option vuoto non supera la validazione lato API: meglio ometterlo
        if (incassato && metodo) {
            payload.metodo_pagamento = optPayload(metodo);
        }

        setSaving(true);
        try {
            const res = await DataManager.updateItem('prenotazione', payload, item.id);
            if (res && res.success === 1) {
                toast.success('Prenotazione aggiornata.', { theme: 'colored' });
                navigate(elenco);
            }
            else {
                toast.error(messaggioErrore(res, 'Salvataggio non riuscito.'), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSaving(false);
        }
    }

    async function annulla() {
        setWorking(true);
        try {
            const res = await BookingManager.cancelBooking(item.id, motivoAnnullamento || null);
            if (res && res.success === 1) {
                toast.success(res.message || 'Prenotazione annullata.', { theme: 'colored' });
                setDaAnnullare(false);
                setMotivoAnnullamento('');
                reload();
            }
            else {
                toast.error(messaggioErrore(res, 'Annullamento non riuscito.'), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setWorking(false);
        }
    }

    const stato = statoLista({ loading: loading, error: error, empty: !item, icon: 'calendar-check' });

    const bottoneAnnulla = annullabile && (
        <button
            type="button"
            className="lv-btn lv-btn-ghost lv-btn-danger-ghost"
            onClick={() => { setDaAnnullare(true); setMotivoAnnullamento(''); }}
        >
            <FontAwesomeIcon icon={['fas', 'ban']} className="me-2" />
            Annulla prenotazione
        </button>
    );

    return (
        <DetailPage
            backTo={elenco}
            backLabel="Torna alle prenotazioni"
            eyebrow={amministratore ? 'Registro prenotazioni' : 'La tua prenotazione'}
            title={item ? `Prenotazione #${item.id}` : 'Prenotazione'}
            subtitle={item ? formatIntervallo(item.inizio, item.fine) : undefined}
        >
            {stato || (
                <>
                    <dl className="lv-detail">
                        <div>
                            <dt>Quando</dt>
                            <dd>{formatIntervallo(item.inizio, item.fine)}</dd>
                        </div>
                        <div>
                            <dt>Sala</dt>
                            <dd>{refLabel(item.idSala, ['nome'])}</dd>
                        </div>
                        {amministratore && (
                            <div>
                                <dt>Condomino</dt>
                                <dd>
                                    {nomeUtente(item.author)}
                                    {item.author && item.author.email ? ` · ${item.author.email}` : ''}
                                </dd>
                            </div>
                        )}
                        <div>
                            <dt>Motivo</dt>
                            <dd>{item.motivo}</dd>
                        </div>
                        <div>
                            <dt>Partecipanti</dt>
                            <dd>{item.num_partecipanti || '—'}</dd>
                        </div>
                        <div>
                            <dt>Tariffa</dt>
                            <dd>{refLabel(item.idTariffa, ['nome'])} · {formatImporto(item.importo)}</dd>
                        </div>
                        <div>
                            <dt>Stato</dt>
                            <dd><span className={`lv-pill ${classePillStato(st)}`}>{umanizza(st)}</span></dd>
                        </div>
                        <div>
                            <dt>Regolamento accettato</dt>
                            <dd>
                                {/* la versione c'e' anche senza accettazione: fa fede la data */}
                                {item.data_accettazione
                                    ? `v${item.regolamento_versione} il ${formatDateTime(item.data_accettazione)}`
                                    : '—'}
                            </dd>
                        </div>
                        {item.note && (
                            <div>
                                <dt>Note del condomino</dt>
                                <dd>{item.note}</dd>
                            </div>
                        )}
                        {st === 'annullata' && (
                            <div>
                                <dt>Annullata</dt>
                                <dd>
                                    {formatDateTime(item.annullata_il)}
                                    {item.motivo_annullamento ? ` · ${item.motivo_annullamento}` : ''}
                                </dd>
                            </div>
                        )}
                        {st === 'annullata' && esitoAnnullamento(item) && (
                            <div>
                                <dt>Pagamento</dt>
                                <dd>
                                    <span className={`lv-pill ${esitoAnnullamento(item).dovuto ? 'lv-pill-warn' : 'lv-pill-ok'}`}>
                                        {esitoAnnullamento(item).label}
                                    </span>
                                </dd>
                            </div>
                        )}
                    </dl>

                    {amministratore ? (
                        <form onSubmit={salva}>
                            <div className="lv-subsection">
                                <div className="lv-subsection-head">
                                    <h3 className="lv-subsection-title">Incasso e note interne</h3>
                                </div>
                                <div className="lv-form-grid">
                                    <Field label="Incasso">
                                        <Select
                                            value={pagato}
                                            onChange={setPagato}
                                            options={[
                                                { value: '0', label: 'Da incassare' },
                                                { value: '1', label: 'Pagato' }
                                            ]}
                                        />
                                    </Field>
                                    <Field label="Data del pagamento" hint="Vuoto = data odierna.">
                                        <input
                                            type="date"
                                            className="lv-input"
                                            value={dataPagamento}
                                            disabled={parseInt(pagato, 10) !== 1}
                                            onChange={e => setDataPagamento(e.target.value)}
                                        />
                                    </Field>
                                    <Field label="Metodo">
                                        <Select
                                            value={metodo}
                                            disabled={parseInt(pagato, 10) !== 1}
                                            onChange={setMetodo}
                                            options={METODI}
                                        />
                                    </Field>
                                    <Field label="Note interne" wide hint="Visibili solo all'amministrazione.">
                                        <textarea
                                            className="lv-input"
                                            rows={3}
                                            value={noteAdmin}
                                            onChange={e => setNoteAdmin(e.target.value)}
                                        />
                                    </Field>
                                </div>
                            </div>

                            <DetailActions saving={saving} submitLabel="Salva">
                                {bottoneAnnulla}
                            </DetailActions>
                        </form>
                    ) : bottoneAnnulla && (
                        <div className="lv-detail-actions">{bottoneAnnulla}</div>
                    )}
                </>
            )}

            <ConfirmModal
                show={daAnnullare}
                title="Annullare la prenotazione?"
                message={item
                    ? `${amministratore ? nomeUtente(item.author) : refLabel(item.idSala, ['nome'])} · ${formatIntervallo(item.inizio, item.fine)}`
                    : ''}
                confirmLabel="Annulla prenotazione"
                danger
                working={working}
                onConfirm={annulla}
                onClose={() => setDaAnnullare(false)}
            >
                {/* la regola del preavviso vale per chiunque annulli */}
                {item && fuoriTermine(item) && (
                    <div className="lv-note lv-note-warn">
                        <FontAwesomeIcon icon={['fas', 'triangle-exclamation']} className="me-2" />
                        Oltre il termine di preavviso: l&apos;importo potrebbe restare dovuto.
                    </div>
                )}
                <label className="lv-field mt-3">
                    <span className="lv-label">
                        {amministratore ? "Motivo dell'annullamento" : 'Motivo (facoltativo)'}
                    </span>
                    <textarea
                        className="lv-input"
                        rows={3}
                        value={motivoAnnullamento}
                        onChange={e => setMotivoAnnullamento(e.target.value)}
                        placeholder={amministratore
                            ? 'Comunicato al condomino nello storico della prenotazione.'
                            : undefined}
                    />
                </label>
            </ConfirmModal>
        </DetailPage>
    );
}
